import {
  Button,
  ButtonColor,
  ButtonSize,
  Combobox,
  LoaderInformation,
  Option,
  Table,
  TableRow,
} from '@code/innovance-react-ui-kit';
import { yupResolver } from '@hookform/resolvers';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import {
  Card,
  Document,
  DocumentStorage,
  Product,
  ProductOffer,
  ProductOfferPageValidation,
} from 'src/front/api/apiInstance';
import {
  ModelError,
  ProductPageBaseData,
  ScoringResultType,
  UploadDocumentResponse,
  UploadType,
} from 'src/front/api/generated';

import infoIcon from 'src/front/assets/icon_info.svg';
import displayPDF from 'src/front/common/utils/domUtils';

import { isInvalidFileError } from 'src/front/common/utils/errorUtils';

import { dictionaryLineFormat, optionFormat } from 'src/front/common/utils/formUtils';
import convertProductOfferBaseDataIntoProductOfferComboData, {
  defaultComboList,
  ProductOfferComboData,
  ProductOfferComboList,
  ProductOfferData,
  ProductOfferFormData,
} from 'src/front/common/utils/productOfferUtils';
import { ProductOfferSchema } from 'src/front/common/utils/validationSchemas';
import AsyncButton from 'src/front/components//AsyncButton';
import SuccessButtonText from 'src/front/components//SuccessButtonText';
import UploadDocumentForm, {
  UploadDocumentFormData,
} from 'src/front/components//UploadDocumentForm';
import {
  ButtonsRow,
  Container,
  Form,
  FormElement,
  FormRow,
  InfoLabel,
  LineSeparator,
  LongFormRow,
  Separator,
  Subtitle,
  TableWrapper,
  Title,
  WarningContainer,
  WarningIcon,
  WarningText,
} from 'src/front/components/FormStyles';
import {
  useCustomerSearchState,
  useProductOfferDebitDispatch,
  useScoringDispatch,
  useSelectedProductOfferDispatch,
  useSelectedProductOfferState,
} from 'src/front/contexts/CardEntryContext';
import styled from 'styled-components';

const StyledButton = styled(AsyncButton)`
  padding-left: 40px;

  text-align: left;
`;

const defaultValues = {
  customerType: null,
  customerSubType: null,
  debitFlag: null,
  resident: null,
  accountType: null,
  brandOwner: null,
  productCardType: null,
};

const productOfferTableHeaders = [
  'general_customerName_label',
  'general_status_label',
  'cardEntry_cardInfo_appDetailProductOffer_entryDate_label',
  'cardEntry_cardInfo_appDetailProductOffer_limit_label',
  'cardEntry_productInfo_debit_label',
  'cardEntry_cardInfo_appDetailProductOffer_currency_label',
  'cardEntry_productInfo_customerType_label',
  'cardEntry_productInfo_customerSubType_label',
  'cardEntry_cardInfo_appDetailProductOffer_referenceNumber_label',
];
const PRODUCT_OFFER_DOCUMENT_TYPE = 'BO';

interface ProductOfferProps {
  goToNextStep?: () => void;
  goToFailureStep?: (message: string, status?: ScoringResultType) => void;
  isEdit?: boolean;
  disablePage?: boolean;
}

const ProductOffers: FC<ProductOfferProps> = ({
  goToNextStep,
  goToFailureStep,
  isEdit,
  disablePage,
}) => {
  const selectedOffer = useSelectedProductOfferState();

  const [customerName, setCustomerName] = useState<string>('');
  const [uploadedDocument, setUploadedDocument] = useState<UploadDocumentResponse>();
  const [innerDocument, setInnerDocument] = useState<UploadDocumentResponse>();
  const [uploadError, setUploadError] = useState<ModelError>();
  const [printWarning, setPrintWarning] = useState<string>();
  const [printSuccess, setPrintSuccess] = useState<boolean>(false);
  const [selectedProductOffer, setSelectedProductOffer] = useState<TableRow | undefined>(
    selectedOffer
  );
  const [productOfferDataList, setProductOfferDataList] = useState<ProductOfferData[]>([]);
  const [customerTypes, setCustomerTypes] = useState<Option[]>([]);
  const [customerSubTypes, setCustomerSubTypes] = useState<Option[]>([]);
  const [debitFlags, setDebitFlags] = useState<Option[]>([]);
  const [residents, setResidents] = useState<Option[]>([]);
  const [accountTypes, setAccountTypes] = useState<Option[]>([]);
  const [brandOwners, setBrandOwners] = useState<Option[]>([]);
  const [productCardTypes, setProductCardTypes] = useState<Option[]>([]);

  const productOfferDataListRef = useRef<ProductOfferData[]>(productOfferDataList);
  const productOfferDataRef = useRef<ProductOfferFormData>(defaultValues);
  const innerDocumentRef = useRef<UploadDocumentResponse>();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { customerId } = useCustomerSearchState();

  const { t } = useTranslation();

  const scoringDispatch = useScoringDispatch();
  const productOfferDispatch = useSelectedProductOfferDispatch();
  const isDebitDispatch = useProductOfferDebitDispatch();

  const { watch, errors, handleSubmit, formState, setValue, control } = useForm<
    ProductOfferFormData
  >({
    defaultValues,
    resolver: yupResolver(ProductOfferSchema),
  });

  useEffect(() => {
    productOfferDataListRef.current = productOfferDataList;
  }, [productOfferDataList]);

  useEffect(() => {
    innerDocumentRef.current = innerDocument;
  }, [innerDocument]);

  useEffect(() => {
    productOfferDataRef.current = {
      accountType: watch('accountType'),
      brandOwner: watch('brandOwner'),
      customerSubType: watch('customerSubType'),
      customerType: watch('customerType'),
      debitFlag: watch('debitFlag'),
      productCardType: watch('productCardType'),
      resident: watch('resident'),
    };
  }, [watch]);

  useEffect(() => {
    const init = async (): Promise<void> => {
      const { data } = await Card.getCardCustomerInfo();
      if (data.result === 'OK') {
        setCustomerName(data.customerName);
      }
    };
    init();
  }, []);

  useEffect(() => {
    return () => {
      ProductOfferPageValidation.validateProductOfferPageData({
        productOfferData: {
          productOfferDataList: productOfferDataListRef.current,
          physicalFileName: innerDocumentRef.current?.physicalFileName,
          productOfferDocumentId: innerDocumentRef.current?.documentId,
          isItScoredForProductService: !!innerDocumentRef.current,
        },
        productData: {
          accountType: dictionaryLineFormat(productOfferDataRef.current.accountType),
          brandOwner: dictionaryLineFormat(productOfferDataRef.current.brandOwner),
          customerSubType: dictionaryLineFormat(productOfferDataRef.current.customerSubType),
          customerType: dictionaryLineFormat(productOfferDataRef.current.customerType),
          debitFlag: dictionaryLineFormat(productOfferDataRef.current.debitFlag),
          productCardType: dictionaryLineFormat(productOfferDataRef.current.productCardType),
          resident: dictionaryLineFormat(productOfferDataRef.current.resident),
        },
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fillComboList = (productOfferList: ProductOfferComboData[]): void => {
    const comboDataList: ProductOfferComboList = defaultComboList;
    productOfferList.forEach((productOffer) => {
      comboDataList.accountTypes.add(optionFormat(productOffer.accountType));
      comboDataList.brandOwners.add(optionFormat(productOffer.brandOwner));
      comboDataList.customerTypes.add(optionFormat(productOffer.customerType));
      comboDataList.customerSubTypes.add(optionFormat(productOffer.customerSubType));
      comboDataList.debitFlags.add(optionFormat(productOffer.debitFlag));
      comboDataList.productCardTypes.add(optionFormat(productOffer.productCardType));
      comboDataList.residents.add(optionFormat(productOffer.resident));
    });
    fillCombos(comboDataList);
  };

  useEffect(() => {
    const getPageData = async (): Promise<void> => {
      if (disablePage) {
        const { data } = await ProductOfferPageValidation.getProductOfferPageData();
        if (
          data.result === 'OK' &&
          data.pageData &&
          data.pageData.productOfferData.productOfferDataList
        ) {
          setProductOfferDataList(
            data.pageData.productOfferData.productOfferDataList.map((productData) => {
              return {
                id: productData.rowId.toString(),
                ...productData,
              };
            })
          );

          fillComboList(
            convertProductOfferBaseDataIntoProductOfferComboData(
              data.pageData.productOfferData.productOfferDataList
            )
          );
          await selectCombos(data.pageData?.productData);
        }
      } else if (isEdit) {
        const { data } = await ProductOfferPageValidation.getProductOfferPageData();
        if (data.result === 'OK' && data.pageData) {
          if (data.pageData.productData) {
            const {
              brandOwner,
              accountType,
              debitFlag,
              customerSubType,
              customerType,
              productCardType,
              resident,
            } = data.pageData.productData;
            fillComboList([
              {
                brandOwner,
                accountType,
                productCardType,
                debitFlag,
                customerType,
                customerSubType,
                resident,
              },
            ]);
            await selectCombos(data.pageData?.productData);
          }
        }
      }
    };
    getPageData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const selectCombos = useCallback(
    async (productOffer?: ProductPageBaseData): Promise<void> => {
      [
        {
          name: 'customerType',
          value: optionFormat(productOffer?.customerType || { code: '', value: '' }),
        },
        {
          name: 'customerSubType',
          value: optionFormat(productOffer?.customerSubType || { code: '', value: '' }),
        },
        {
          name: 'debitFlag',
          value: optionFormat(productOffer?.debitFlag || { code: '', value: '' }),
        },
        {
          name: 'resident',
          value: optionFormat(productOffer?.resident || { code: '', value: '' }),
        },
        {
          name: 'accountType',
          value: optionFormat(productOffer?.accountType || { code: '', value: '' }),
        },
        {
          name: 'brandOwner',
          value: optionFormat(productOffer?.brandOwner || { code: '', value: '' }),
        },
        {
          name: 'productCardType',
          value: optionFormat(productOffer?.productCardType || { code: '', value: '' }),
        },
      ].forEach(({ name, value }) => setValue(name, value as {} | undefined));

      await Product.getProductLineDetails({
        customerTypeCode: productOffer?.customerType.code || '',
        customerSubTypeCode: productOffer?.customerSubType.code || '',
        debitFlagCode: productOffer?.debitFlag.code || '',
        residentCode: productOffer?.resident.code || '',
        accountTypeCode: productOffer?.accountType.code || '',
        brandOwnerCode: productOffer?.brandOwner.code || '',
        cardTypeCode: productOffer?.productCardType.code || '',
      });
    },
    [setValue]
  );

  const documentSubmit = async (formData: UploadDocumentFormData): Promise<void> => {
    if (innerDocument?.documentId) {
      await Document.deleteDocument({ documentId: innerDocument?.documentId });
    }
    try {
      const { data } = await Document.uploadDocuments(
        formData.files?.[0],
        PRODUCT_OFFER_DOCUMENT_TYPE,
        UploadType.DOCUMENTUPLOAD,
        formData.explanation,
        customerId
      );
      if (data.result === 'OK') {
        setInnerDocument(data);
      }
    } catch (error) {
      if (error && isInvalidFileError(error)) {
        setUploadError(error);
      }
    }
  };

  const selectBankOffer = async (): Promise<void> => {
    setIsLoading(true);
    const { data } = await ProductOffer.getProductOffersWithScore();
    if (data.isProductOfferRejected) {
      goToFailureStep && goToFailureStep(data.message || '', ScoringResultType.AUTOREJECT);
    } else if (data.result === 'OK') {
      scoringDispatch(true);
      setProductOfferDataList(
        data.productOfferDataList.map((productData) => {
          return {
            id: productData.rowId.toString(),
            ...productData,
          };
        })
      );
      fillComboList(
        convertProductOfferBaseDataIntoProductOfferComboData(data.productOfferDataList)
      );
    }
    setIsLoading(false);
  };

  const fillCombos = async (productOffers: ProductOfferComboList): Promise<void> => {
    setCustomerTypes(Array.from(productOffers.customerTypes));
    setCustomerSubTypes(Array.from(productOffers.customerSubTypes));
    setDebitFlags(Array.from(productOffers.debitFlags));
    setResidents(Array.from(productOffers.residents));
    setAccountTypes(Array.from(productOffers.accountTypes));
    setBrandOwners(Array.from(productOffers.brandOwners));
    setProductCardTypes(Array.from(productOffers.productCardTypes));
  };

  const printQuestionare = async (): Promise<void> => {
    const { data } = await ProductOffer.generateProductOfferDocument();
    if (data.result === 'OK') {
      const { data: response } = await DocumentStorage.getDocument({
        documentId: data.documentId,
      });
      if (response.result === 'OK') {
        displayPDF(response.documentData);
      }
      setPrintSuccess(true);
      printWarning && setPrintWarning(undefined);
    }
  };
  const getProductOffer = useCallback((productOffer?: TableRow): ProductOfferData | undefined => {
    return productOfferDataListRef.current.find((product) => product.id === productOffer?.id);
  }, []);

  useEffect(() => {
    const productOffer = getProductOffer(selectedProductOffer);
    if (productOffer) {
      const {
        accountType,
        residentFlag: resident,
        brandOwner,
        customerType,
        customerSubType,
        debitFlag,
        cardType: productCardType,
      } = productOffer;

      selectCombos({
        accountType,
        resident,
        brandOwner,
        customerType,
        customerSubType,
        debitFlag,
        productCardType,
      });
    }
  }, [getProductOffer, selectCombos, selectedOffer, selectedProductOffer]);
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleContinue = async (): Promise<void> => {};
  const onProductOfferSelect = async (item?: TableRow): Promise<void> => {
    setSelectedProductOffer(item);
    productOfferDispatch(item);
  };
  const onSubmit = (): void => {
    isDebitDispatch(watch('debitFlag')?.value === '01');
    goToNextStep && goToNextStep();
  };

  const productOfferTableData = productOfferDataList.map((productOffer) => ({
    id: productOffer.id,
    columns: [
      productOffer.customerName,
      productOffer.cardStatus,
      productOffer.entryDate,
      productOffer.cardLimit,
      productOffer.debitFlag.value,
      productOffer.accountType.value,
      productOffer.customerType.value,
      productOffer.customerSubType.value,
      productOffer.referenceNumber,
    ],
  }));

  return (
    <Container>
      {isLoading ? (
        <LoaderInformation information={t('general_transactionInProgress_errorMessage')} />
      ) : null}
      <Title>
        <Trans i18nKey="cardEntry_cardInfo_appDetail_title" />
      </Title>
      {customerName ? (
        <>
          <FormRow>
            <LineSeparator />
          </FormRow>

          <div>
            <InfoLabel>
              <Trans i18nKey="cardEntry_customerTable_name_title" />:
            </InfoLabel>
            {customerName}
          </div>
          <FormRow>
            <LineSeparator />
          </FormRow>
        </>
      ) : null}
      {!(disablePage || isEdit) ? (
        <>
          <Subtitle>
            <Trans i18nKey="cardEntry_productInfo_productOffer_printBankOfferText_text" />
          </Subtitle>
          <ButtonsRow>
            <StyledButton
              color={ButtonColor.Secondary}
              onClick={printQuestionare}
              size={ButtonSize.Large}
              text={
                printSuccess ? (
                  <SuccessButtonText
                    isSuccess={printSuccess}
                    label={t('cardEntry_productInfo_productOffer_printBankOfferDocument_button')}
                  />
                ) : (
                  t('cardEntry_productInfo_productOffer_printBankOfferDocument_button')
                )
              }
              type="button"
            />
          </ButtonsRow>
          <Separator />
          <FormRow>
            <FormElement>
              <UploadDocumentForm
                disableUploadButton={!printSuccess}
                error={uploadError}
                handleContinue={handleContinue}
                setUploadedDocument={setUploadedDocument}
                submit={documentSubmit}
                uploadedDocument={uploadedDocument}
                hideContinueButton
              />
            </FormElement>
          </FormRow>
          {printWarning ? (
            <LongFormRow>
              <WarningText>
                <Trans i18nKey={printWarning} />
              </WarningText>
            </LongFormRow>
          ) : null}
          <ButtonsRow>
            <AsyncButton
              color={ButtonColor.Primary}
              disabled={!innerDocument}
              onClick={selectBankOffer}
              size={ButtonSize.Large}
              text={t('cardEntry_cardInfo_appDetail_productOfferSelection_button')}
              type="button"
            />
          </ButtonsRow>
        </>
      ) : (
        <Form onSubmit={handleSubmit(onSubmit)}>
          {isEdit ? null : (
            <>
              <WarningContainer>
                <WarningIcon src={infoIcon} />
                <Subtitle>
                  <Trans i18nKey="cardEntry_cardInfo_appDetailProductOffer_productOfferWarning_text" />
                </Subtitle>
              </WarningContainer>
              <Separator />
              <TableWrapper>
                <Table
                  emptyTableText={t('cardEntry_documentEntry_noUploadedDocumentFound_text')}
                  headers={productOfferTableHeaders.map((head) => t(head))}
                  items={productOfferTableData}
                  onSelect={onProductOfferSelect}
                  pageNumber={1}
                  selectedItem={selectedProductOffer}
                  widths={['15', '11', '11', '8', '12', '8', '13', '13', '9']}
                  withRadio
                />
              </TableWrapper>
              <Separator />
            </>
          )}
          {(selectedProductOffer && selectedProductOffer.columns[1] === 'APPROVED') || isEdit ? (
            <>
              <Subtitle>
                <Trans i18nKey="cardEntry_productInfo_productDetail_text" />
              </Subtitle>
              <FormRow>
                <FormElement>
                  <Controller
                    control={control}
                    name="customerType"
                    render={({ onChange, value }) => (
                      <Combobox
                        errorMessage={t(errors?.customerType?.message as string)}
                        items={customerTypes}
                        label={t('cardEntry_productInfo_customerType_label')}
                        name="customerType"
                        onChange={onChange}
                        value={value}
                        disabled
                        required
                      />
                    )}
                  />
                </FormElement>
              </FormRow>
              <FormRow>
                <FormElement>
                  <Controller
                    control={control}
                    name="customerSubType"
                    render={({ onChange, value }) => (
                      <Combobox
                        errorMessage={t(errors?.customerSubType?.message as string)}
                        items={customerSubTypes}
                        label={t('cardEntry_productInfo_customerSubType_label')}
                        name="customerSubType"
                        onChange={onChange}
                        value={value}
                        disabled
                        required
                      />
                    )}
                  />
                </FormElement>
              </FormRow>
              <FormRow>
                <FormElement>
                  <Controller
                    control={control}
                    name="debitFlag"
                    render={({ onChange, value }) => (
                      <Combobox
                        errorMessage={t(errors?.debitFlag?.message as string)}
                        items={debitFlags}
                        label={t('cardEntry_productInfo_debit_label')}
                        name="debitFlag"
                        onChange={onChange}
                        value={value}
                        disabled
                        required
                      />
                    )}
                  />
                </FormElement>
              </FormRow>
              <FormRow>
                <FormElement>
                  <Controller
                    control={control}
                    name="resident"
                    render={({ onChange, value }) => (
                      <Combobox
                        errorMessage={t(errors?.resident?.message as string)}
                        items={residents}
                        label={t('cardEntry_productInfo_resident_label')}
                        name="resident"
                        onChange={onChange}
                        value={value}
                        disabled
                        required
                      />
                    )}
                  />
                </FormElement>
              </FormRow>
              <FormRow>
                <FormElement>
                  <Controller
                    control={control}
                    name="accountType"
                    render={({ onChange, value }) => (
                      <Combobox
                        errorMessage={t(errors?.accountType?.message as string)}
                        items={accountTypes}
                        label={t('cardEntry_productInfo_accountType_label')}
                        name="accountType"
                        onChange={onChange}
                        value={value}
                        disabled
                        required
                      />
                    )}
                  />
                </FormElement>
              </FormRow>
              <FormRow>
                <FormElement>
                  <Controller
                    control={control}
                    name="brandOwner"
                    render={({ onChange, value }) => (
                      <Combobox
                        errorMessage={t(errors?.brandOwner?.message as string)}
                        items={brandOwners}
                        label={t('cardEntry_productInfo_brandOwner_label')}
                        name="brandOwner"
                        onChange={onChange}
                        value={value}
                        disabled
                        required
                      />
                    )}
                  />
                </FormElement>
              </FormRow>
              <FormRow>
                <FormElement>
                  <Controller
                    control={control}
                    name="productCardType"
                    render={({ onChange, value }) => (
                      <Combobox
                        errorMessage={t(errors?.productCardType?.message as string)}
                        items={productCardTypes}
                        label={t('cardEntry_productInfo_productCardType_label')}
                        name="productCardType"
                        onChange={onChange}
                        value={value}
                        disabled
                        required
                      />
                    )}
                  />
                </FormElement>
              </FormRow>
              <Button
                color={ButtonColor.Primary}
                disabled={formState.isSubmitting}
                size={ButtonSize.Medium}
                text={t('general_continue_button')}
                type="submit"
              />
            </>
          ) : null}
        </Form>
      )}
    </Container>
  );
};

export default ProductOffers;
